import React, { useState, useEffect, useRef } from 'react';
import { Bell, X, AlertTriangle, AlertCircle, Info, CheckCheck } from 'lucide-react';
import { useAuth } from '../../context/AuthContext';
import { notificationsAPI } from '../../services/api';
import { useNavigate } from 'react-router-dom';

export const NotificationBell = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [isOpen, setIsOpen] = useState(false);
  const [notifications, setNotifications] = useState([]);
  const [loading, setLoading] = useState(false);
  const panelRef = useRef(null);

  const unreadCount = notifications.filter((n) => !n.is_read).length;

  const fetchNotifications = async () => {
    if (!user?.id) return;
    setLoading(true);
    try {
      const data = await notificationsAPI.getAll(user.id);
      setNotifications(data);
    } catch (e) {
      // silent fail — don't break sidebar
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchNotifications();
    const interval = setInterval(fetchNotifications, 5 * 60 * 1000);
    return () => clearInterval(interval);
  }, [user?.id]);

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (panelRef.current && !panelRef.current.contains(e.target)) {
        setIsOpen(false);
      }
    };
    if (isOpen) document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [isOpen]);

  const handleMarkRead = async (notifId) => {
    if (!user?.id) return;
    try {
      await notificationsAPI.markRead(notifId, user.id);
      setNotifications((prev) =>
        prev.map((n) => (n.id === notifId ? { ...n, is_read: true } : n))
      );
    } catch (e) {
      // silent
    }
  };

  const handleMarkAllRead = async () => {
    if (!user?.id) return;
    try {
      await notificationsAPI.markAllRead(user.id);
      setNotifications((prev) => prev.map((n) => ({ ...n, is_read: true })));
    } catch (e) {
      // silent
    }
  };

  const handleItemClick = async (notif) => {
    await handleMarkRead(notif.id);
    setIsOpen(false);
    if (notif.reference_path) navigate(notif.reference_path);
  };

  const getIcon = (severity) => {
    if (severity === 'critical')
      return <AlertCircle className="w-4 h-4 text-red-400 flex-shrink-0 mt-0.5" />;
    if (severity === 'warning')
      return <AlertTriangle className="w-4 h-4 text-yellow-400 flex-shrink-0 mt-0.5" />;
    return <Info className="w-4 h-4 text-blue-400 flex-shrink-0 mt-0.5" />;
  };

  return (
    <div className="relative" ref={panelRef}>
      {/* Bell button */}
      <button
        data-testid="notification-bell"
        onClick={() => { setIsOpen(!isOpen); if (!isOpen) fetchNotifications(); }}
        className="relative flex items-center justify-center w-9 h-9 rounded-lg text-white/60 hover:text-white hover:bg-white/10 transition-all duration-200"
        title="Notifications"
      >
        <Bell className="w-4.5 h-4.5" />
        {unreadCount > 0 && (
          <span
            data-testid="notification-unread-count"
            className="absolute -top-1 -right-1 min-w-[17px] h-[17px] bg-red-500 text-white text-[9px] font-bold rounded-full flex items-center justify-center px-0.5 leading-none"
          >
            {unreadCount > 9 ? '9+' : unreadCount}
          </span>
        )}
      </button>

      {/* Notification panel */}
      {isOpen && (
        <div
          data-testid="notification-panel"
          className="absolute bottom-12 left-0 w-[320px] bg-[#1a2035] border border-white/10 rounded-xl shadow-2xl overflow-hidden z-50"
          style={{ boxShadow: '0 20px 60px rgba(0,0,0,0.5)' }}
        >
          {/* Header */}
          <div className="flex items-center justify-between px-4 py-3 border-b border-white/10">
            <div className="flex items-center gap-2">
              <h3 className="text-[13px] font-semibold text-white">Notifications</h3>
              {unreadCount > 0 && (
                <span className="bg-red-500/20 text-red-400 text-[10px] font-semibold px-1.5 py-0.5 rounded-full">
                  {unreadCount} new
                </span>
              )}
            </div>
            <div className="flex items-center gap-1.5">
              {unreadCount > 0 && (
                <button
                  onClick={handleMarkAllRead}
                  data-testid="mark-all-read-btn"
                  className="text-[11px] text-lime/80 hover:text-lime flex items-center gap-1 transition-colors"
                >
                  <CheckCheck className="w-3.5 h-3.5" />
                  All read
                </button>
              )}
              <button
                onClick={() => setIsOpen(false)}
                className="text-white/30 hover:text-white/70 transition-colors ml-1"
              >
                <X className="w-4 h-4" />
              </button>
            </div>
          </div>

          {/* List */}
          <div className="max-h-[400px] overflow-y-auto">
            {loading ? (
              <div className="p-8 text-center text-white/30 text-[13px]">
                <div className="w-5 h-5 border-2 border-white/20 border-t-lime rounded-full animate-spin mx-auto mb-2" />
                Loading...
              </div>
            ) : notifications.length === 0 ? (
              <div className="p-8 text-center">
                <Bell className="w-10 h-10 text-white/10 mx-auto mb-3" />
                <p className="text-white/30 text-[13px]">All caught up!</p>
                <p className="text-white/20 text-[11px] mt-0.5">No active reminders</p>
              </div>
            ) : (
              notifications.map((notif) => (
                <button
                  key={notif.id}
                  data-testid={`notification-item-${notif.id}`}
                  onClick={() => handleItemClick(notif)}
                  className={`w-full text-left px-4 py-3.5 border-b border-white/[0.06] hover:bg-white/[0.04] transition-colors flex gap-3 ${
                    notif.is_read ? 'opacity-40' : ''
                  }`}
                >
                  {getIcon(notif.severity)}
                  <div className="flex-1 min-w-0">
                    <p
                      className={`text-[12px] font-semibold leading-snug ${
                        notif.is_read ? 'text-white/60' : 'text-white'
                      }`}
                    >
                      {notif.title}
                    </p>
                    <p className="text-[11px] text-white/40 mt-0.5 line-clamp-2 leading-relaxed">
                      {notif.message}
                    </p>
                  </div>
                  {!notif.is_read && (
                    <div className="w-2 h-2 rounded-full bg-lime flex-shrink-0 mt-1" />
                  )}
                </button>
              ))
            )}
          </div>

          {/* Footer */}
          {notifications.length > 0 && (
            <div className="px-4 py-2.5 border-t border-white/10 bg-white/[0.02]">
              <p className="text-[10px] text-white/35 text-center">
                PO expiry (&lt;30 days) &amp; feedback overdue (&gt;7 days) &amp; invoiced month due &amp; trainer overload
              </p>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default NotificationBell;
